import {
	ButtonBase,
	Dialog,
	TableView,
	Toolbar
} from "client/Components";
import { KnowledgeItemPinModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { generateTrackingId } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useState
} from "react";
import { unPin } from "client/Api/KnowledgeItemManagement/knowledgeItemManagement";
import styles from "./PinnedArticlesDialog.module.less";
import { KnowledgeItemStatusToLocalizationKeyMap } from "../../HubManagementDialog/Content/KnowledgeItemsContent/KnowledgeItemsOverview";

export type PinnedArticlesDialogProps = {
	pinnedItems: readonly KnowledgeItemPinModel[];
	onClose(wasUpdated?: boolean): void;
	categoryName: string;
};

const {
	Cell,
	Row
} = TableView;

export const PinnedArticlesDialog = (props: PinnedArticlesDialogProps): ReactElement => {
	const {
		pinnedItems,
		categoryName,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [unPinnedItems, setUnPinnedItems] = useState<Set<number>>(new Set());
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const canBeSaved: boolean = unPinnedItems.size > 0;

	const pinUnpinItem = useCallback(
		(item: KnowledgeItemPinModel): void => {
			setUnPinnedItems(
				unPinnedItems => {
					const copy: Set<number> = new Set(unPinnedItems);
					const isPinned: boolean = !copy.has(item.id);

					if(isPinned) {
						copy.add(item.id);
					} else {
						copy.delete(item.id);
					}

					return copy;
				}
			);
		},
		[]
	);

	const confirmationHandler = async(): Promise<void> => {
		setIsBusy(true);

		try {
			await unPin(Array.from(unPinnedItems), globalCancellation);
			setIsBusy(false);
			onClose(true);
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);
			alertManager.showError(localize("_GeneralErrorMessage"));
			console.error(e);
		}
	};

	const tableHeader: string = `${localize("_PinnedArticlesFor")} \`${categoryName}\``;

	return(
		<Dialog
			trackingId="pinned-articles-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_NoMoreItemsCanBePinnedInThisCategory")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<p>{localize("_TheMaximumNumberOfPinnedItemsPerCategory")}</p>
				<h4
					className={styles.title}
					title={tableHeader}
				>
					{tableHeader}
				</h4>
				<TableView
					trackingId="pinned-articles"
					items={pinnedItems}
					itemKey="id"
					columns={[
						{
							title: localize("_Title"),
							trackingId: "title",
							width: "fill"
						},
						{
							title: localize("_Status"),
							trackingId: "status",
							width: "fill"
						},
						{
							title: localize("_Action"),
							trackingId: "action",
							width: "fill"
						}
					]}
					hasZebraPrinting
				>
					{(c: KnowledgeItemPinModel) => (
						<Row
							trackingId={generateTrackingId("pinned-article-row", `|${c.id}`)}
						>
							<Cell>{c.title}</Cell>
							<Cell>{KnowledgeItemStatusToLocalizationKeyMap[c.status]}</Cell>
							<Cell>
								<ButtonBase
									onClick={() => pinUnpinItem(c)}
									className={styles.button}
									trackingId={unPinnedItems.has(c.id)
										? "pin"
										: "unpin"
									}
								>
									{
										unPinnedItems.has(c.id) ?
										localize("_Pin") :
										localize("_Unpin")
									}
								</ButtonBase>
							</Cell>
						</Row>
					)}
				</TableView>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={confirmationHandler}
						text={localize("_Save")}
						trackingId="save"
						isDisabled={!canBeSaved}
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
PinnedArticlesDialog.displayName = "PinnedArticlesDialog";

export type PinnedArticlesDialogInitialData = Omit<
	PropsWithChildren<PinnedArticlesDialogProps>,
	"onClose"
>;

export function usePinnedArticlesDialog(): [
	element: ReactElement | null,
	open: (props: PinnedArticlesDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => {
			const onCloseHandler = (wasUpdated?: boolean): void => {
				if (wasUpdated === true) {
					onSave();
				} else {
					onClose();
				}
			};

			return (
				<PinnedArticlesDialog
					{...props}
					onClose={onCloseHandler}
				/>
			);
		}
	);
}